import React, {
  MouseEvent,
} from 'react';
import {
  NpiDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import {
  useDeleteBillingConfig,
} from 'views/EMR/Settings/hooks';
import {
  BillingConfigType,
  DeleteBillingConfigParams,
} from 'views/EMR/Settings/BillingConfigurations/types';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Tooltip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import Spinner from 'pages/Dashboard/components/Spinner';
import DeleteIcon from 'core/icons/delete';
import clsx from 'clsx';

type NPICellProps = {
  npi?: NpiDTO | null;
};

export function NPICell({ npi }: NPICellProps) {
  return isNil(npi) ? <span className="text-gray-700">—</span> : (
    <div className="flex gap-1 items-center text-gray-700 !truncate">
      <span className="flex after:inline-block after:relative after:ml-1 after:content-['•'] last:after:!hidden">
        {npi?.type}
      </span>
      <span className="flex after:inline-block after:relative after:ml-1 after:content-['•'] last:after:!hidden">
        {npi?.number}
      </span>
      <span className="flex after:inline-block after:relative after:ml-1 after:content-['•'] last:after:!hidden">
        {npi?.taxonomyCode}
      </span>
    </div>
  );
}

type DeleteConfigCellProps = {
  type: BillingConfigType;
  payload: DeleteBillingConfigParams;
  configTitle: string;
  canDelete: boolean;
  tooltip?: string;
};

export const billingConfigEventsByType = Object.freeze({
  serviceFacility: 'onServiceFacilityChange',
  renderingProvider: 'onRenderingProviderChange',
  billingProvider: 'onBillingProviderChange',
  billingProviderRule: 'onBillingProviderRuleChange',
  billingProviderPtan: 'onBillingProviderPtanChange',
});

export function DeleteConfigCell({
  type,
  payload,
  configTitle,
  canDelete,
  tooltip,
}: DeleteConfigCellProps) {
  const { mutateAsync: deleteProviderRule, isPending } = useDeleteBillingConfig(type);
  const { confirm, closeConfirm } = useConfirmDialog();
  const notify = useNotify();

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      content: `Are you sure you want to delete this ${configTitle.toLowerCase()}?`,
      onConfirm: async () => {
        await deleteProviderRule(payload);
        settingsEventChannel.emit(billingConfigEventsByType[type]);
        closeConfirm();
        notify({
          message: `${configTitle} deleted successfully.`,
          variant: 'success',
        });
      },
      title: `Delete ${configTitle}`,
      confirmText: 'Confirm',
    });
  };

  return (
    <div className="w-full flex gap-1 items-center justify-end">
      <Tooltip title={tooltip ?? `Delete ${configTitle}`}>
        <span>
          <IconButton
            onClick={handleDelete}
            size="small"
            color="error"
            className={clsx('!p-0', {
              '!cursor-not-allowed !pointer-events-auto opacity-30': !canDelete,
            })}
            disabled={!canDelete}
          >
            {isPending ? (
              <Spinner className="!w-5 !h-5" />
            ) : (
              <DeleteIcon className="!w-5 !h-5 text-gray-700" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
